'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchMoreAssetsForCarousel } from '../components/actions';
import { CarouselElementType } from '../types';

const PAGES_TO_PREFETCH = 2;

export const useCarouselData = (
  elementId: string,
  locale: string,
  itemsPerPage: number,
) => {
  const itemsPerFetch = Math.ceil(itemsPerPage * PAGES_TO_PREFETCH);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['carousel', elementId, locale],
    queryFn: ({ pageParam = 1 }) =>
      fetchMoreAssetsForCarousel(elementId, locale, pageParam, itemsPerFetch),
    getNextPageParam: (lastFetchedPage, allFetchedPages) => {
      const currentPageNumber = allFetchedPages.length;
      const totalPages = lastFetchedPage?.meta?.totalPages ?? 1;
      const hasReachedEnd =
        currentPageNumber >= totalPages ||
        (lastFetchedPage?.elements?.length ?? 0) < itemsPerFetch;

      if (hasReachedEnd) {
        return undefined; // Stop fetching
      }

      return currentPageNumber + 1; // Fetch next page
    },
    initialPageParam: 1,
    staleTime: 0.5 * 60 * 1000, // Stale after 30s (for regular refresh)
    gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  const allPageElements: CarouselElementType[] =
    data?.pages.flatMap((page) => page?.elements ?? []) ?? [];

  return {
    elements: allPageElements,
    isLoading: isLoading || isFetchingNextPage,
    hasMoreData: hasNextPage ?? false,
    error: error?.message ?? null,
    fetchMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
  };
};
